// @ts-check
import React from 'react';

import Stack from '@liquidweb-dev/undercurrent/Stack';
import Typography from '@liquidweb-dev/undercurrent/Typography';
import TypographyOverflow from '@liquidweb-dev/undercurrent/TypographyOverflow';
import CPUIcon from '@liquidweb-dev/undercurrent/icons/lucide/Cpu';
import HardDriveIcon from '@liquidweb-dev/undercurrent/icons/lucide/HardDrive';
import MemoryStickIcon from '@liquidweb-dev/undercurrent/icons/lucide/MemoryStick';

import DetermineCountryFlag from 'containers/pages/shop/ConfigPackage/DetermineCountryFlag';

import OSIcon from 'components/standard/OSIcon';
import dataCenterMap from 'containers/pages/shop/ConfigPackage/dataCenterMap';
import { stripOsName } from 'modules/queries/asset/useDetails';

/**
 * @param {Object} props
 * @param {import('modules/queries/asset/useDetails').AssetDetails | undefined} props.data
 */
const AssetOverview = ({ data }) => {
	const {
		domain,
		ip,
		cpu,
		os: osFallback,
		primaryStorage,
		ramFormatted: ram,
		region_id: regionId,
		template,
	} = data || {};

	const {
		os_name: osName,
		os_bit: osBit,
		os_version: osVersion,
	} = template || {};

	const osFormatted = template
		? `${stripOsName(osName || '')} ${osVersion}, ${osBit}-bit`
		: osFallback;

	const location = regionId ? dataCenterMap[regionId]?.label : undefined;
	const country = regionId ? dataCenterMap[regionId]?.country : undefined;

	return (
		<>
			<Stack direction="row" justifyContent="space-between">
				<TypographyOverflow variant="title-xs" fontWeight={500}>
					{domain}
				</TypographyOverflow>
				<Typography
					variant="body-sm"
					fontWeight={400}
					sx={(theme) => ({ color: theme.vars.palette.uc.text.weak })}
				>
					{ip}
				</Typography>
			</Stack>
			<Stack direction="row" gap={200} flexWrap="wrap">
				<Stack direction="row" alignItems="center" gap={50}>
					{country && (
						<DetermineCountryFlag
							country={country}
							sx={{
								height: ({ size }) => size.xS,
								width: ({ size }) => size.xS,
							}}
						/>
					)}
					<Typography variant="label-lg" fontWeight={500}>
						{location}
					</Typography>
				</Stack>
				<Stack direction="row" alignItems="center" gap={50}>
					<OSIcon
						os={osName || osFallback || ''}
						sx={{
							height: ({ size }) => size.xS,
							width: ({ size }) => size.xS,
						}}
					/>
					<Typography variant="label-lg" fontWeight={500}>
						{osFormatted}
					</Typography>
				</Stack>
				{cpu && (
					<Stack direction="row" alignItems="center" gap={50}>
						<CPUIcon
							sx={{
								height: ({ size }) => size.xS,
								width: ({ size }) => size.xS,
							}}
						/>
						<Typography variant="label-lg" fontWeight={500}>
							{cpu}
						</Typography>
					</Stack>
				)}
				{ram && (
					<Stack direction="row" alignItems="center" gap={50}>
						<MemoryStickIcon
							sx={{
								height: ({ size }) => size.xS,
								width: ({ size }) => size.xS,
							}}
						/>
						<Typography variant="label-lg" fontWeight={500}>
							{ram} Memory
						</Typography>
					</Stack>
				)}
				{primaryStorage && (
					<Stack direction="row" alignItems="center" gap={50}>
						<HardDriveIcon
							sx={{
								height: ({ size }) => size.xS,
								width: ({ size }) => size.xS,
							}}
						/>
						<Typography variant="label-lg" fontWeight={500}>
							{primaryStorage} Storage
						</Typography>
					</Stack>
				)}
			</Stack>
		</>
	);
};

export default AssetOverview;
